interface Config {
  domain: string;
  baseUrl: string;
  mktUrl: string;
  marketplaceApiUrl: string;
  auth0: {
    internalDomain: string;
    externalDomain: string;
    externalClientId: string;
    audience: string;
  };
  urls: {
    appRoot: string;
    apiRoot: string;
    wsRoot: string;
    prsApiRoot: string;
  };
  launchDarklyClientId: string;
  gtm: {
    gtmId: string;
    auth: string;
    preview: string;
  };
  rudderstack: {
    writeKey: string;
    dataPlaneUrl: string;
    cookieDomain?: string;
  };
  finix: {
    environment: string;
    merchantId: string;
    applicationId: string;
  };
  finixPayout: {
    environment: string;
    merchantId: string;
    applicationId: string;
  };
}
type Environments = 'test' | 'development' | 'staging' | 'production';

type Configs = {
  [env in Environments]: Config;
};

// this helps test locally on mobile devices
// replace with your ip address; Mac: open terminal and type `ipconfig getifaddr en0`
// You'll need to also add this the baseUrl(http://${devBase}:3001) to the Application URIs/Allowed Origins in the auth0 dashboard
const devBase = 'localhost';
const apiPort = process.env.REACT_APP_API_PORT || '8080';
const comPort = process.env.REACT_APP_COM_PORT || '3001';
const marketplaceApiUrlOverride = process.env.REACT_APP_MARKETPLACE_API_URL;
const prsApiRootOverride = process.env.REACT_APP_PRS_API_URL;

export const configs: Configs = {
  test: {
    domain: 'leaseend.com',
    baseUrl: 'https://my.staging.leaseend.com',
    mktUrl: 'https://www.staging.leaseend.com',
    marketplaceApiUrl: 'https://marketplace.staging.leaseend.com',
    auth0: {
      internalDomain: 'auth.leaseend.com',
      externalDomain: 'auth-external.leaseend.com',
      externalClientId: 'sYRfwn3aLpgaLXEM3EZfFao1BsShvGlZ',
      audience: 'https://api.leaseend.com/graphql',
    },
    urls: {
      appRoot: 'https://localhost:3000',
      apiRoot: 'http://localhost:8080',
      wsRoot: 'ws://localhost:8080',
      prsApiRoot: 'https://payoff.staging.leaseend.com',
    },
    launchDarklyClientId: '',
    gtm: {
      gtmId: '',
      auth: '',
      preview: '',
    },
    rudderstack: {
      writeKey: '',
      dataPlaneUrl: '',
      cookieDomain: '',
    },
    finix: {
      environment: 'sandbox',
      merchantId: '',
      applicationId: '',
    },
    finixPayout: {
      environment: 'sandbox',
      merchantId: '',
      applicationId: '',
    },
  },
  development: {
    domain: devBase,
    baseUrl: `http://${devBase}:${comPort}`,
    mktUrl: `http://${devBase}:3002`,
    marketplaceApiUrl: marketplaceApiUrlOverride || 'https://marketplace.staging.leaseend.com',
    auth0: {
      internalDomain: 'auth.leaseend.com',
      externalDomain: 'auth-external.leaseend.com',
      externalClientId: 'sYRfwn3aLpgaLXEM3EZfFao1BsShvGlZ',
      audience: 'https://api.leaseend.com/graphql',
    },
    urls: {
      appRoot: `http://${devBase}:3000`,
      apiRoot: `http://${devBase}:${apiPort}`,
      wsRoot: `ws://${devBase}:${apiPort}`,
      prsApiRoot: prsApiRootOverride || 'https://payoff.staging.leaseend.com',
    },
    launchDarklyClientId: '618400a0e330d26355a8e1e1',
    gtm: {
      gtmId: 'GTM-PSLHHMM',
      auth: 'bgchO0mYpBcZfNkiuwRMpg',
      preview: 'env-4',
    },
    rudderstack: {
      writeKey: '2TgHgjnec2WRfo63YDTAzuF03D0',
      dataPlaneUrl: 'https://leaseenddiodkq.dataplane.rudderstack.com',
      cookieDomain: '',
    },
    finix: {
      environment: 'sandbox',
      merchantId: 'MUeDVrf2ahuKc9Eg5TeZugvs',
      applicationId: 'APagXdeXhvL7MkwTcp8rJyZE',
    },
    finixPayout: {
      environment: 'sandbox',
      merchantId: 'MUv88q3sBnBQHzJYxH91LP5b',
      applicationId: 'APnawW1YWJDqCTfafWWsR2ts',
    },
  },
  staging: {
    domain: 'leaseend.com',
    baseUrl: 'https://my.staging.leaseend.com',
    mktUrl: 'https://www.staging.leaseend.com',
    marketplaceApiUrl: 'https://marketplace.staging.leaseend.com',
    auth0: {
      internalDomain: 'auth.leaseend.com',
      externalDomain: 'auth-external.leaseend.com',
      externalClientId: 'sYRfwn3aLpgaLXEM3EZfFao1BsShvGlZ',
      audience: 'https://api.leaseend.com/graphql',
    },
    urls: {
      appRoot: 'https://app.staging.leaseend.com',
      apiRoot: 'https://api.staging.leaseend.com',
      wsRoot: 'wss://api.staging.leaseend.com',
      prsApiRoot: 'https://payoff.staging.leaseend.com',
    },
    launchDarklyClientId: '618400a0e330d26355a8e1e1',
    gtm: {
      gtmId: 'GTM-PSLHHMM',
      auth: 'ulxXBrhuMcRevnSvDodg3A',
      preview: 'env-5',
    },
    rudderstack: {
      writeKey: '2TgHgjnec2WRfo63YDTAzuF03D0',
      dataPlaneUrl: 'https://leaseenddiodkq.dataplane.rudderstack.com',
      cookieDomain: '.leaseend.com',
    },
    finix: {
      environment: 'sandbox',
      merchantId: 'MUeDVrf2ahuKc9Eg5TeZugvs',
      applicationId: 'APagXdeXhvL7MkwTcp8rJyZE',
    },
    finixPayout: {
      environment: 'sandbox',
      merchantId: 'MUv88q3sBnBQHzJYxH91LP5b',
      applicationId: 'APnawW1YWJDqCTfafWWsR2ts',
    },
  },
  production: {
    domain: 'leaseend.com',
    baseUrl: 'https://my.leaseend.com',
    mktUrl: 'https://www.leaseend.com',
    marketplaceApiUrl: 'https://marketplace.leaseend.com',
    auth0: {
      internalDomain: 'auth.leaseend.com',
      externalDomain: 'auth-external.leaseend.com',
      externalClientId: 'sYRfwn3aLpgaLXEM3EZfFao1BsShvGlZ',
      audience: 'https://api.leaseend.com/graphql',
    },
    urls: {
      appRoot: 'https://app.leaseend.com',
      apiRoot: 'https://api.leaseend.com',
      wsRoot: 'wss://api.leaseend.com',
      prsApiRoot: 'https://payoff.leaseend.com',
    },
    launchDarklyClientId: '618400a0e330d26355a8e1e2',
    gtm: {
      gtmId: 'GTM-PSLHHMM',
      auth: 'AkAE3X1T7WsBjCHxN-ROwQ',
      preview: 'env-1',
    },
    rudderstack: {
      writeKey: '2ThxH3bH4rZEYGkKriUCMUIrp5D',
      dataPlaneUrl: 'https://leaseenddiodkq.dataplane.rudderstack.com',
      cookieDomain: '.leaseend.com',
    },
    finix: {
      environment: 'live',
      merchantId: 'MUcpr1YX6Lk8YuDaQxdabnhe',
      applicationId: 'AP8u5B6AVU37pBzHsM4TrnLN',
    },
    finixPayout: {
      environment: 'live',
      merchantId: 'MUa7CEQvxiUo7cQVYAHWACTN',
      applicationId: 'APq8AHXXfQw8u8GCjYAcYroM',
    },
  },
};

export const getEnv = () => process.env.REACT_APP_STAGE || process.env.NODE_ENV;
export const isLocal = getEnv() === 'development';
export const isStaging = getEnv() === 'staging';
export const isProd = getEnv() === 'production';

export default configs[getEnv() as Environments];
